import type { PreviewTabKey } from "@/stores/shot-panel-store"
import type { VisitOptions } from "@inertiajs/core"
import { router } from "@inertiajs/react"

/** Matches backend `shot_short_label`: "Сц.N шот M" (ru) or "Scene N shot M" (en) */
const SHOT_SUBJECT_LABEL_RE = /^(.+?):\s*((?:Сц\.\d+\s+шот\s+[\d.]+)|(?:Scene\s+\d+\s+shot\s+[\d.]+))\s*$/u

/** Matches backend `canvas_generate.with_id`: "Холст: #123" (ru/en prefix) */
const CANVAS_SUBJECT_LABEL_RE = /^(Холст|Canvas):\s*#(\d+)/u

export type JobSubjectLabelSource = {
  job_kind: string
  status?: string | null
  subject_label?: string | null
  shot_ids?: number[]
  canvas_generation_id?: number | null
  canvas_generation_number?: number | null
  canvas_id?: number | null
  /** Company board owner from job meta — open /company/canvas, not jobs-host project. */
  canvas_company_id?: number | null
  sketch_edit_id?: number | null
  sketch_file_name?: string | null
  sketch_shot_tab?: "storyboard" | "shooting_board" | null
  character_id?: number | null
  prop_id?: number | null
  location_id?: number | null
  subject_type?: string | null
  subject_id?: number | null
  skill_import_id?: number | null
  skills_base?: "project" | "studio" | "company" | null
  skills_project_id?: number | null
  /** Original / proxy image for completed face jobs (Sketch view). */
  preview_media_url?: string | null
  preview_thumb_url?: string | null
}

/** Scope for visitCanvasGeneration / canvasGenerationHref from a job row. */
export function canvasVisitScope(
  job: Pick<JobSubjectLabelSource, "canvas_company_id">,
  projectId: number,
): { kind: "company"; companyId: number } | number {
  if (job.canvas_company_id != null && job.canvas_company_id > 0) {
    return { kind: "company", companyId: job.canvas_company_id }
  }
  return projectId
}

const CHARACTER_LINKABLE_JOB_KINDS = new Set([
  "character_reference",
  "character_sheet",
  "character_portrait",
  "character_emotion_sheet",
  "character_scale_pose",
  "character_expression_sheet",
  "character_gait_reference",
  "character_action_beat",
  "character_voice_demo",
  "seedance_avatar_registration",
])

const PROP_LINKABLE_JOB_KINDS = new Set(["prop_reference", "prop_front_view"])

export function parseJobSubjectLabel(label: string): { prefix: string; shotLabel: string } | null {
  const m = label.match(SHOT_SUBJECT_LABEL_RE)
  if (!m) return null
  return { prefix: m[1], shotLabel: m[2] }
}

export function resolveJobCharacterId(job: JobSubjectLabelSource): number | null {
  const fromMeta = job.character_id
  if (fromMeta != null && fromMeta > 0) return fromMeta
  if (job.subject_type === "Character" && job.subject_id != null && job.subject_id > 0) return job.subject_id
  return null
}

export function canOpenCharacter(job: JobSubjectLabelSource): boolean {
  if (job.job_kind === "sketch_edit") {
    return resolveJobCharacterId(job) != null
  }
  return CHARACTER_LINKABLE_JOB_KINDS.has(job.job_kind) && resolveJobCharacterId(job) != null
}

export function resolveJobPropId(job: JobSubjectLabelSource): number | null {
  const fromMeta = job.prop_id
  if (fromMeta != null && fromMeta > 0) return fromMeta
  if (job.subject_type === "Prop" && job.subject_id != null && job.subject_id > 0) return job.subject_id
  return null
}

export function canOpenProp(job: JobSubjectLabelSource): boolean {
  if (job.job_kind === "sketch_edit") {
    return resolveJobPropId(job) != null
  }
  return PROP_LINKABLE_JOB_KINDS.has(job.job_kind) && resolveJobPropId(job) != null
}

export function resolveJobLocationId(job: JobSubjectLabelSource): number | null {
  const fromMeta = job.location_id
  if (fromMeta != null && fromMeta > 0) return fromMeta
  if (job.subject_type === "Location" && job.subject_id != null && job.subject_id > 0) return job.subject_id
  return null
}

export function canOpenLocation(job: JobSubjectLabelSource): boolean {
  if (job.job_kind === "sketch_edit") {
    return resolveJobLocationId(job) != null
  }
  return job.job_kind === "location_reference" && resolveJobLocationId(job) != null
}

/** Completed location wall/floor face job — open Sketch view with original media. */
export function canOpenLocationSceneFace(job: JobSubjectLabelSource): boolean {
  if (job.job_kind !== "location_scene_face") return false
  if (job.status !== "completed") return false
  return Boolean(job.preview_media_url?.trim())
}

export function locationWorkspaceHref(projectId: number, locationId: number, variantId?: number | null): string {
  const variant = variantId == null ? "" : `/variant-${variantId}`
  return `/projects/${projectId}/workspace/locations#location-${locationId}${variant}`
}

export function visitLocation(projectId: number, locationId: number, options?: VisitOptions): void {
  router.visit(locationWorkspaceHref(projectId, locationId), options)
}

export function parseCharacterJobSubjectLabel(
  label: string,
): { prefix: string; characterName: string; suffix: string | null } | null {
  const m = label.match(/^(.+?):\s*(.+)$/u)
  if (!m) return null
  const rest = m[2].trim()
  const outfitSep = rest.match(/^(.+?)\s+—\s+(.+)$/u)
  if (outfitSep) {
    return { prefix: m[1], characterName: outfitSep[1].trim(), suffix: outfitSep[2].trim() }
  }
  return { prefix: m[1], characterName: rest, suffix: null }
}

export function characterWorkspaceHref(projectId: number, characterId: number, outfitId?: number | null): string {
  const outfit = outfitId == null ? "" : `/outfit-${outfitId}`
  return `/projects/${projectId}/workspace/characters#character-${characterId}${outfit}`
}

export function visitCharacter(projectId: number, characterId: number, options?: VisitOptions): void {
  router.visit(characterWorkspaceHref(projectId, characterId), options)
}

export function propWorkspaceHref(projectId: number, propId: number, stateId?: number | null): string {
  const state = stateId == null ? "" : `/state-${stateId}`
  return `/projects/${projectId}/workspace/props#prop-${propId}${state}`
}

export function visitProp(projectId: number, propId: number, options?: VisitOptions): void {
  router.visit(propWorkspaceHref(projectId, propId), options)
}

export function charactersScaleSheetHref(projectId: number): string {
  return `/projects/${projectId}/workspace/characters?scale_sheet=1`
}

export function visitCharactersScaleSheet(projectId: number, options?: VisitOptions): void {
  router.visit(charactersScaleSheetHref(projectId), options)
}

export function propsScaleSheetHref(projectId: number): string {
  return `/projects/${projectId}/workspace/props?scale_sheet=1`
}

export function visitPropsScaleSheet(projectId: number, options?: VisitOptions): void {
  router.visit(propsScaleSheetHref(projectId), options)
}

export function canOpenCharactersScaleSheet(job: JobSubjectLabelSource): boolean {
  return job.job_kind === "scale_sheet_compose"
}

export function canOpenPropsScaleSheet(job: JobSubjectLabelSource): boolean {
  return job.job_kind === "prop_scale_sheet_compose"
}

/** Mixed sheet compose is shot-scoped; open video tab when exactly one shot_id. */
export function canOpenMixedScaleSheet(job: JobSubjectLabelSource): boolean {
  if (job.job_kind !== "mixed_scale_sheet_compose") return false
  const ids = (job.shot_ids ?? []).filter((id) => id > 0)
  return ids.length === 1
}

export function resolveMixedScaleSheetShotId(job: JobSubjectLabelSource): number | null {
  if (!canOpenMixedScaleSheet(job)) return null
  return (job.shot_ids ?? []).find((id) => id > 0) ?? null
}

export function resolveSkillImportId(job: JobSubjectLabelSource): number | null {
  const id = job.skill_import_id
  if (id != null && id > 0) return id
  return null
}

export function canOpenSkillImport(_job: JobSubjectLabelSource): boolean {
  // file_to_skill jobs are informational only — resume via skills UI, not job subject links.
  return false
}

export type SkillsBase = "project" | "studio" | "company"

export function resolveSkillsBase(job: JobSubjectLabelSource): SkillsBase {
  const base = job.skills_base
  if (base === "studio" || base === "company" || base === "project") return base
  return "project"
}

export function skillImportSettingsHref(
  projectId: number,
  importId: number,
  skillsBase: SkillsBase = "project",
  skillsProjectId?: number | null,
): string {
  if (skillsBase === "studio") {
    return `/studio/skills?skill_import=${importId}`
  }
  if (skillsBase === "company") {
    return `/company/skills?skill_import=${importId}`
  }
  const pid = skillsProjectId != null && skillsProjectId > 0 ? skillsProjectId : projectId
  return `/projects/${pid}/workspace/skills?skill_import=${importId}`
}

export function visitSkillImport(
  projectId: number,
  importId: number,
  options?: VisitOptions & { skillsBase?: SkillsBase; skillsProjectId?: number | null },
): void {
  const { skillsBase = "project", skillsProjectId = null, ...visitOptions } = options ?? {}
  router.visit(skillImportSettingsHref(projectId, importId, skillsBase, skillsProjectId), visitOptions)
}

export function canOpenCanvasGeneration(job: JobSubjectLabelSource): boolean {
  return (
    (job.job_kind === "canvas_generate" || job.job_kind === "sketch_edit") &&
    job.canvas_generation_id != null &&
    job.canvas_generation_id > 0 &&
    job.canvas_id != null &&
    job.canvas_id > 0
  )
}

export function resolveSketchEditId(job: JobSubjectLabelSource): number | null {
  const id = job.sketch_edit_id
  if (id != null && id > 0) return id
  return null
}

export function resolveSketchFileName(job: JobSubjectLabelSource): string | null {
  const name = job.sketch_file_name?.trim()
  if (name) return name
  const label = job.subject_label?.trim()
  if (!label) return null
  const m = label.match(/^(?:Корректировка|Correction):\s*(.+)$/u)
  return m?.[1]?.trim() || null
}

export function canOpenSketchEdit(job: JobSubjectLabelSource, opts?: { canEditContent?: boolean }): boolean {
  if (opts?.canEditContent === false) return false
  if (resolveJobCharacterId(job) != null) return false
  if (resolveJobPropId(job) != null) return false
  if (resolveJobLocationId(job) != null) return false
  return job.job_kind === "sketch_edit" && job.status === "completed" && resolveSketchEditId(job) != null
}

export function parseCanvasJobSubjectLabel(label: string): { prefix: string; generationId: number } | null {
  const m = label.match(CANVAS_SUBJECT_LABEL_RE)
  if (!m) return null
  const generationId = Number(m[2])
  if (!Number.isFinite(generationId) || generationId <= 0) return null
  return { prefix: m[1], generationId }
}

export function canvasJobDisplayNumber(job: JobSubjectLabelSource): number | null {
  const generationId = job.canvas_generation_id
  if (generationId == null || generationId <= 0) return null
  const label = job.subject_label?.trim()
  const parsed = label ? parseCanvasJobSubjectLabel(label) : null
  if (parsed) return parsed.generationId
  const number = job.canvas_generation_number
  if (number != null && number > 0) return number
  return generationId
}

export function canvasJobSubjectPrefix(job: JobSubjectLabelSource, fallbackPrefix: string): string {
  const label = job.subject_label?.trim()
  if (!label) return fallbackPrefix
  const parsed = parseCanvasJobSubjectLabel(label)
  if (parsed) return parsed.prefix
  const legacyPrefix = label.match(/^(Холст|Canvas):/)
  if (legacyPrefix) return legacyPrefix[1]
  return fallbackPrefix
}

const STORYBOARD_JOB_KINDS = new Set(["storyboard", "storyboard_correction"])

const SHOOTING_BOARD_JOB_KINDS = new Set(["shooting_board", "animatic_segment_shooting_board_strip"])

const VIDEO_JOB_KINDS = new Set([
  "video",
  "byteplus_seedance_video_correction",
  "topaz_post_production",
  "scale_sheet_compose",
  "prop_scale_sheet_compose",
  "mixed_scale_sheet_compose",
  "shot_timeline_render",
  "shot_timeline_lipsync",
])

export function previewTabForJobKind(
  jobKind: string,
  sketchShotTab?: "storyboard" | "shooting_board" | null,
): PreviewTabKey {
  if (jobKind === "sketch_edit") {
    return sketchShotTab === "shooting_board" ? "shooting_board" : "storyboard"
  }
  if (SHOOTING_BOARD_JOB_KINDS.has(jobKind)) return "shooting_board"
  if (STORYBOARD_JOB_KINDS.has(jobKind)) return "storyboard"
  if (VIDEO_JOB_KINDS.has(jobKind)) return "video"
  return "video"
}
